import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Iter42 — BS month/year picker that also shows the AD equivalent date range.
 *  Used by Financial Rules → New Target and the Approval Center view.
 *
 *  Props (controlled):
 *    bsYear  : selected BS year (number or "")
 *    bsMonth : selected BS month (1..12) (number or "")
 *    onChange({ bs_year, bs_month, bs_month_name, ad_start, ad_end, ad_month_year, bs_period_label })
 *      - Fires both on direct user picks AND once the AD range is fetched.
 */
export default function BSPeriodPicker({ bsYear, bsMonth, onChange, label = "Target Period (Nepali Calendar)" }) {
  const [opts, setOpts] = useState({ years: [], months: [], current: {} });
  const [range, setRange] = useState(null);

  useEffect(() => {
    api.get("/reports/bs-options").then((r) => setOpts(r.data || {})).catch(() => {});
  }, []);

  // Fetch AD range whenever both fields are set
  useEffect(() => {
    const y = parseInt(bsYear);
    const m = parseInt(bsMonth);
    if (!y || !m) { setRange(null); return; }
    let cancelled = false;
    api.get(`/reports/bs-period-range?bs_year=${y}&bs_month=${m}`)
      .then((r) => {
        if (cancelled) return;
        setRange(r.data);
        onChange?.({
          bs_year: y, bs_month: m,
          bs_month_name: r.data.bs_month_name,
          bs_period_label: `${r.data.bs_month_name} ${y}`,
          ad_start: r.data.ad_start,
          ad_end: r.data.ad_end,
          ad_month_year: r.data.ad_month_year,
        });
      })
      .catch(() => !cancelled && setRange(null));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bsYear, bsMonth]);

  const setYear = (v) => onChange?.({ bs_year: parseInt(v) || "", bs_month: parseInt(bsMonth) || "" });
  const setMonth = (v) => onChange?.({ bs_year: parseInt(bsYear) || "", bs_month: parseInt(v) || "" });

  return (
    <div className="space-y-2" data-testid="bs-period-picker">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs text-slate-600 space-y-1">
          BS Year
          <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                  value={bsYear || ""} onChange={(e) => setYear(e.target.value)}
                  data-testid="bs-picker-year">
            <option value="">— Select year —</option>
            {opts.years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 space-y-1">
          BS Month
          <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                  value={bsMonth || ""} onChange={(e) => setMonth(e.target.value)}
                  data-testid="bs-picker-month">
            <option value="">— Select month —</option>
            {opts.months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
      </div>
      {range ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs" data-testid="bs-picker-range">
          <div className="font-semibold text-emerald-900">{range.bs_month_name} {bsYear}</div>
          <div className="text-emerald-800 font-mono">BS: {range.bs_start} to {range.bs_end}</div>
          <div className="text-emerald-700 font-mono">AD: {range.ad_start} to {range.ad_end}</div>
          <div className="text-emerald-600 mt-1">Fiscal Year {range.bs_fiscal_year}</div>
        </div>
      ) : (
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500" data-testid="bs-picker-empty">
          Select a BS year and month to see the AD date range. Target achievement uses this BS month, not the English calendar month.
        </div>
      )}
    </div>
  );
}
